import { FiExternalLink, FiFileText, FiImage, FiTrash2 } from "react-icons/fi";
import { formatBuildDocumentFileSize } from "../utils/buildDocuments";

const VALID_CATEGORIES = ["Maintenance", "Mods", "Warranty", "Other"];

function BuildDocumentCard({ documentItem, onView, onDelete, deleting }) {
  const safeDocument = documentItem || {};
  const title = safeDocument.title || "Untitled document";
  const category = VALID_CATEGORIES.includes(safeDocument.category)
    ? safeDocument.category
    : "Other";
  const fileTypeLabel = getFileTypeLabel(safeDocument.fileType);
  const FileIcon = getFileTypeIcon(safeDocument.fileType);
  const formattedDate = formatDocumentDate(safeDocument.documentDate);
  const formattedFileSize = formatBuildDocumentFileSize(safeDocument.fileSize);
  const hasDescription = Boolean(safeDocument.description?.trim());
  const canView = Boolean(safeDocument.downloadUrl) && typeof onView === "function";
  const canDelete = typeof onDelete === "function" && !deleting;

  const handleView = () => {
    if (!canView) return;
    onView(safeDocument);
  };

  const handleDelete = () => {
    if (!canDelete) return;
    onDelete(safeDocument);
  };

  return (
    <article style={cardStyle}>
      <div style={mainContentStyle}>
        <div style={topRowStyle}>
          <div style={fileTypePillStyle}>
            <FileIcon aria-hidden="true" />
            <span>{fileTypeLabel}</span>
          </div>

          <div style={titleGroupStyle}>
            <h3 style={titleStyle}>{title}</h3>
            <span style={categoryBadgeStyle}>{category}</span>
          </div>
        </div>

        <div style={metadataRowStyle}>
          <span>{formattedDate}</span>

          {safeDocument.fileName && (
            <>
              <span style={metadataDividerStyle}>•</span>
              <span style={fileNameStyle}>{safeDocument.fileName}</span>
            </>
          )}

          <span style={metadataDividerStyle}>•</span>
          <span>{formattedFileSize}</span>
        </div>

        {hasDescription && (
          <p style={descriptionStyle}>{safeDocument.description.trim()}</p>
        )}
      </div>

      <div style={actionsStyle}>
        <button
          type="button"
          onClick={handleView}
          disabled={!canView}
          aria-label={`View ${title}`}
          style={{
            ...viewButtonStyle,
            opacity: canView ? 1 : 0.55,
            cursor: canView ? "pointer" : "not-allowed"
          }}
        >
          <FiExternalLink aria-hidden="true" />
          View
        </button>

        <button
          type="button"
          onClick={handleDelete}
          disabled={!canDelete}
          aria-label={`Delete ${title}`}
          style={{
            ...deleteButtonStyle,
            opacity: canDelete ? 1 : 0.6,
            cursor: canDelete ? "pointer" : "not-allowed"
          }}
        >
          <FiTrash2 aria-hidden="true" />
          {deleting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </article>
  );
}

function formatDocumentDate(documentDate) {
  if (!documentDate || typeof documentDate !== "string") {
    return "Date not provided";
  }

  const parts = documentDate.split("-");

  if (parts.length !== 3) {
    return "Date not provided";
  }

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1000 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return "Date not provided";
  }

  const localDate = new Date(year, month - 1, day);

  if (
    localDate.getFullYear() !== year ||
    localDate.getMonth() !== month - 1 ||
    localDate.getDate() !== day
  ) {
    return "Date not provided";
  }

  return localDate.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}

function getFileTypeLabel(fileType) {
  if (fileType === "application/pdf") return "PDF";
  if (fileType === "image/jpeg") return "JPG";
  if (fileType === "image/png") return "PNG";

  return "FILE";
}

function getFileTypeIcon(fileType) {
  if (fileType === "image/jpeg" || fileType === "image/png") {
    return FiImage;
  }

  return FiFileText;
}

const cardStyle = {
  width: "100%",
  display: "flex",
  alignItems: "stretch",
  justifyContent: "space-between",
  gap: "18px",
  flexWrap: "wrap",
  backgroundColor: "#181818",
  border: "1px solid #2d2d2d",
  borderRadius: "18px",
  padding: "18px",
  color: "#fff",
  boxSizing: "border-box",
  boxShadow: "0 12px 32px rgba(0,0,0,0.22)"
};

const mainContentStyle = {
  flex: "1 1 300px",
  minWidth: 0
};

const topRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  minWidth: 0
};

const fileTypePillStyle = {
  flexShrink: 0,
  minWidth: "58px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  borderRadius: "12px",
  padding: "8px 9px",
  backgroundColor: "#111",
  border: "1px solid #333",
  color: "#e10600",
  fontSize: "12px",
  fontWeight: "900",
  boxSizing: "border-box"
};

const titleGroupStyle = {
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap"
};

const titleStyle = {
  margin: 0,
  color: "#fff",
  fontSize: "18px",
  lineHeight: "1.25",
  fontWeight: "850",
  overflowWrap: "anywhere"
};

const categoryBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: "999px",
  padding: "5px 9px",
  backgroundColor: "rgba(225, 6, 0, 0.14)",
  border: "1px solid rgba(225, 6, 0, 0.38)",
  color: "#ffb4b4",
  fontSize: "12px",
  fontWeight: "850",
  lineHeight: 1
};

const metadataRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "7px",
  marginTop: "12px",
  color: "#aaa",
  fontSize: "13px",
  lineHeight: "1.45",
  minWidth: 0
};

const metadataDividerStyle = {
  color: "#555"
};

const fileNameStyle = {
  overflowWrap: "anywhere",
  minWidth: 0
};

const descriptionStyle = {
  margin: "12px 0 0",
  color: "#d0d0d0",
  fontSize: "14px",
  lineHeight: "1.55",
  whiteSpace: "pre-line",
  overflowWrap: "anywhere"
};

const actionsStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
  flex: "0 1 auto",
  flexWrap: "wrap"
};

const viewButtonStyle = {
  minHeight: "42px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  border: "none",
  borderRadius: "13px",
  padding: "10px 14px",
  backgroundColor: "#e10600",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "850",
  textDecoration: "none",
  boxSizing: "border-box"
};

const deleteButtonStyle = {
  minHeight: "42px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  border: "1px solid #3a3a3a",
  borderRadius: "13px",
  padding: "10px 14px",
  backgroundColor: "#242424",
  color: "#fff",
  fontSize: "14px",
  fontWeight: "850",
  boxSizing: "border-box"
};

export default BuildDocumentCard;